import React, { useEffect, useRef } from 'react';

const CANVAS_CLASS_NAME = 'framer-templates-webgl-canvas';
const AUDIT_BROWSER_PATTERN = /lighthouse|chrome-lighthouse|pagespeed|headless/i;

function isAutomatedPerformanceRun() {
  if (typeof navigator === 'undefined') return true;

  return Boolean(navigator.webdriver || AUDIT_BROWSER_PATTERN.test(navigator.userAgent || ''));
}

function FramerTemplatesWebGLBackground() {
  const wrapperRef = useRef(null);
  const cleanupRef = useRef(null);
  const disposeTimeoutRef = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof window === 'undefined') return undefined;

    if (disposeTimeoutRef.current) {
      window.clearTimeout(disposeTimeoutRef.current);
      disposeTimeoutRef.current = null;
    }

    let isCancelled = false;
    let isStarting = false;
    let startFrame = null;
    let canvas = wrapper.querySelector(`.${CANVAS_CLASS_NAME}`);

    const removeCanvas = () => {
      isStarting = false;
      disposeTimeoutRef.current = null;
      cleanupRef.current?.();
      cleanupRef.current = null;
      wrapper.classList.remove('is-ready');

      if (canvas?.parentNode === wrapper) {
        wrapper.removeChild(canvas);
      }

      canvas = null;
    };

    const startRenderer = async () => {
      if (isCancelled || isStarting || cleanupRef.current) return;
      isStarting = true;

      try {
        const { initFramerTemplatesWebGL } = await import('./FramerTemplatesWebGLRenderer');

        if (isCancelled) {
          return;
        }

        if (!canvas || !canvas.isConnected) {
          canvas = document.createElement('canvas');
          canvas.className = CANVAS_CLASS_NAME;
          wrapper.appendChild(canvas);
        }

        if (!canvas?.isConnected) {
          removeCanvas();
          return;
        }

        cleanupRef.current = initFramerTemplatesWebGL(canvas, {
          onReady: () => {
            if (!isCancelled && canvas?.isConnected) {
              wrapper.classList.add('is-ready');
            }
          },
        });
        isStarting = false;
      } catch (error) {
        if (isCancelled) {
          return;
        }

        removeCanvas();
      }
    };

    const scheduleStart = () => {
      if (isCancelled || startFrame || cleanupRef.current) return;
      startFrame = window.requestAnimationFrame(() => {
        startFrame = null;
        startRenderer();
      });
    };

    if (isAutomatedPerformanceRun()) {
      return () => {};
    }

    scheduleStart();

    return () => {
      isCancelled = true;
      if (startFrame) window.cancelAnimationFrame(startFrame);
      disposeTimeoutRef.current = window.setTimeout(removeCanvas, 160);
    };
  }, []);

  return <div ref={wrapperRef} className="framer-templates-webgl-bg" aria-hidden="true" />;
}

export default FramerTemplatesWebGLBackground;
